// Signal de módulo (per-tab, en memoria) para el rechazo de ROL del backend (REQUIRES_ROOT).
//
// POR QUÉ existe: el FE decide el CTA durable por `isRoot` (useSubscription). Si esa fuente
// diverge del rol que el backend resuelve del JWT (user_type==='admin'), el FE ofrece "cambiar
// la versión" (durable), el PUT vuelve REQUIRES_ROOT (terminal), el outbox revierte el optimista
// y el CTA reaparece → el usuario vuelve a hacer clic → LOOP (visto en grabaciones de Clarity).
//
// Cuando el outbox revierte un switch por REQUIRES_ROOT, marca aquí (commerce+feature) como
// rol-rechazado en esta sesión. El chip y la card consultan el flag y tratan al usuario como
// NO-root efectivo: ofrecen "echa un vistazo" (preview local, sin PUT) en vez del CTA durable.
// Eso ROMPE el loop y le deja una acción que sí funciona. La divergencia ya queda en Sentry
// (captureRolloutDivergence) para confirmar y, si es material, reconciliar la fuente de isRoot.
//
// Mismo patrón que preview.ts: singleton + subscribe para useSyncExternalStore. NO se persiste
// (es un hecho de sesión). Keyed por `${commerce_id}:${feature_key}` para no contagiar entre
// comercios (multisucursal) ni entre features.

type Listener = () => void;

const rejected = new Set<string>();
// Listeners keyed por feature_key (el subscribe del componente no conoce el commerce activo de
// antemano; re-evalúa isRoleRejected(commerceId, featureKey) en cada notificación).
const listeners = new Map<string, Set<Listener>>();

function stateKey(commerceId: string, featureKey: string): string {
  return `${commerceId}:${featureKey}`;
}

function getListeners(featureKey: string): Set<Listener> {
  let set = listeners.get(featureKey);
  if (!set) {
    set = new Set();
    listeners.set(featureKey, set);
  }
  return set;
}

function notify(featureKey: string): void {
  getListeners(featureKey).forEach((l) => l());
}

export function isRoleRejected(commerceId: string | null, featureKey: string): boolean {
  if (!commerceId) return false;
  return rejected.has(stateKey(commerceId, featureKey));
}

export function subscribeRoleRejection(featureKey: string, listener: Listener): () => void {
  const set = getListeners(featureKey);
  set.add(listener);
  return () => {
    set.delete(listener);
  };
}

// Lo llama el outbox al revertir un switch por REQUIRES_ROOT (rechazo terminal de rol).
export function markRoleRejected(commerceId: string | null, featureKey: string): void {
  if (!commerceId) return;
  const key = stateKey(commerceId, featureKey);
  if (rejected.has(key)) return;
  rejected.add(key);
  notify(featureKey);
}

// Reset de PRODUCCIÓN: al cambiar de sesión/sucursal el hecho no debe sobrevivir (otro usuario
// puede sí ser root). Coherente con el resto de resets del módulo.
export function resetRoleRejectionState(): void {
  const keys = Array.from(listeners.keys());
  rejected.clear();
  keys.forEach((k) => notify(k));
}

// Solo para tests: limpia el estado de módulo entre casos.
export function __resetRoleRejectionForTests(): void {
  rejected.clear();
  listeners.clear();
}
